// File: resources/js/Pages/Sos2/ScriptSnout.jsx
import React, { useState, useCallback, useMemo, useEffect, useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';

import SunburstChart from './SunburstChart';
import { setLevelHighlight } from './useSunburstChart';
import NodePanel from './NodePanel';
import TableView from './TableView';
import Controls from './Controls';
import RollupPanel, { GrainTabs } from './RollupPanel';
import SynthesisPanel from './SynthesisPanel';
import AxisPatterns from './AxisPatterns';
import { METRIC_LABELS } from './Constants';
import SunburstLegend from './SunburstLegend';
import { WikiDrawer } from './InfoUI';
import { OVERVIEW } from './wikiContent';
import PageIntro from './PageIntro';
import { ReadTransition } from './ReadHeader';
import InsightList from './InsightList';
import { prepareChartData } from './prepareChartData';
import { buildSosTour, shouldAutoStartSosTour } from './sosTour';
import { availableGrains } from './rollupData';
import { normalizeUnitText, unitChipLabel, unitDisplayLabel, unitSortKey, unitTextCandidates } from './unitLabels';
import { grainTitle } from './grainNouns';
import './grades.css';
import { tip } from './Tip';

// Phone = a narrow portrait viewport OR a short (landscape-phone) one. Drives the mobile layout:
// stack the read ABOVE the wheel, drop the sticky/clipped right pane, hide scenes by default, and
// open node details in a full-screen modal. Desktop is untouched.
// Phone = narrow (portrait) OR short-AND-not-wide (landscape phone). The "and (max-width:950px)"
// keeps a short BUT wide desktop window from being mistaken for a phone.
const PHONE_Q = '(max-width: 767.98px), (max-width: 950px) and (max-height: 500px)';
const phoneNow = () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(PHONE_Q).matches;
function useIsPhone() {
  const [phone, setPhone] = useState(phoneNow);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(PHONE_Q);
    const on = () => setPhone(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on); };
  }, []);
  return phone;
}

const NAVY = '#14536e';
const HEADER_H = 56;   // shared height so the left + right column headers align on one divider line
// 'overview' is a TAB, not a grain, so it keeps its literal; the three real grains are named by
// the format (grainNouns.js). A stage play's rollup header must not say 'Sequences rollup' about
// the playwright's printed scenes.
const grainHeading = (script, g) => (g === 'overview' ? 'Overview' : grainTitle(script, g, 'Overview'));

export default function ScriptSnout ({ movie }) {
  const isPhone = useIsPhone();
  const readPaneRef = useRef(null);                       // the right/above pane anchor in the sunburst layout
  const [includeScenes, setIncludeScenes] = useState(() => !phoneNow());  // scenes OFF by default on phones (cramped)
  const [metric, setMetric]               = useState('overall');
  const [leftView, setLeftView]           = useState('sunburst'); // 'sunburst' | 'table' | 'patterns'
  const [tableDrawer, setTableDrawer]     = useState(false);       // table mode: detail drawer hidden until asked
  const [patternAxis, setPatternAxis]     = useState(null);        // Patterns mode: a single axis cross-level view (null = habits rollup)

  // Right-panel state: the rollup is the HOME view; a node detail is a
  // drill-down you reach by clicking a wedge or a rollup unit, with Back.
  const [view, setView]                   = useState('rollup'); // 'rollup' | 'node'
  const [rollupGrain, setRollupGrain]     = useState('overview');
  const [selectedNode, setSelectedNode]   = useState(null);     // detailedContent for InfoPanel
  const [selectedId, setSelectedId]       = useState(null);     // sunburst node id for the focus ring
  // True only when the node detail was reached FROM the rollup (a rollup unit/card
  // or stepping within a grain) — drives whether the "‹ Back to … rollup" button
  // shows. A wedge click or a table cell opens the node directly, so no Back.
  const [cameFromRollup, setCameFromRollup] = useState(false);

  // DER synthesis (plain-first read) + footprint-mode highlight on the wheel.
  const synthesis = movie.der_synthesis || null;
  // Insight → "Take me ↓": set the live wheel's Colour-by to the insight's metric and scroll to it.
  const exploreMetric = useCallback((metric) => {
    if (metric && metric !== 'overall') setMetric(metric);
    const el = document.getElementById('sos-wheel-row');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);
  const [footprintIds, setFootprintIds] = useState([]);   // wheel node ids lit by the active pattern footprint
  const [activeCardIdx, setActiveCardIdx] = useState(null);
  // Right pane is two persistent tabs: the prescriptive Synthesis read (default) and the Patterns
  // explorer. A node detail opens over the active tab with a ‹ Back that returns to it.
  const [rightTab, setRightTab] = useState(synthesis ? 'synthesis' : 'patterns');

  // Grey out units where the selected axis is only lightly relevant ("not owed"), so the
  // genuine problems stand out. On by default; the Controls checkbox lets the writer turn it off.
  const [dimNotOwed, setDimNotOwed] = useState(true);
  // Lifted wiki drawer — reachable from the colour-by (i), the "How to read this" help link, and
  // anywhere else, not just from inside a node detail.
  const [wikiArt, setWikiArt] = useState(null);
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);

  const toggleScenes = () => setIncludeScenes(prev => !prev);
  const openMobileDetail = useCallback(() => {
    if (isPhone) setMobileDetailOpen(true);
  }, [isPhone]);
  const closeMobileDetail = useCallback(() => {
    setMobileDetailOpen(false);
    setView('rollup');
    setSelectedId(null);
    setSelectedNode(null);
    setCameFromRollup(false);
  }, []);

  useEffect(() => {
    if (!mobileDetailOpen) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') closeMobileDetail(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mobileDetailOpen, closeMobileDetail]);
  useEffect(() => {
    if (!mobileDetailOpen || !isPhone) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [mobileDetailOpen, isPhone]);

  // The SoS surface mounts BELOW the site navbar, so a flat 100vh runs its bottom
  // off-screen — the right panel can never scroll its last rows into view (#204).
  // Measure the actual distance from this root's top to the viewport bottom and
  // pin the height to exactly that, so both columns end at the fold and scroll fully.
  const rootRef = useRef(null);
  const [fitH, setFitH] = useState('100vh');
  useLayoutEffect(() => {
    const measure = () => {
      const el = rootRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      // A fixed bar pinned to the viewport bottom (e.g. the Laravel DebugBar in dev)
      // overlays the last rows so they can't scroll into view. Reserve its handle
      // height (capped — an expanded panel shouldn't crush the surface). 0 in prod.
      const dbg = document.querySelector('.phpdebugbar');
      const dbgH = dbg && getComputedStyle(dbg).display !== 'none'
        ? Math.min(44, Math.round(dbg.getBoundingClientRect().height)) : 0;
      setFitH(`${Math.max(360, Math.round(window.innerHeight - top - dbgH))}px`);
    };
    measure();
    const t = setTimeout(measure, 400);   // re-measure once the DebugBar has mounted
    window.addEventListener('resize', measure);
    return () => { clearTimeout(t); window.removeEventListener('resize', measure); };
  }, [leftView]);

  // SoS is a full-screen app surface — suppress the marketing footer and the
  // layout's bottom spacing (which crowd the wheel and create stray scroll), and
  // clip the page to the viewport. Restored on unmount so other pages are intact.
  useEffect(() => {
    const footer = document.querySelector('footer');
    const main = document.querySelector('.container-fluid.main');
    // footer carries Bootstrap's d-flex (display:flex !important), so override with !important
    if (footer) footer.style.setProperty('display', 'none', 'important');
    if (main) main.classList.remove('pb-4', 'mb-4');
    return () => {
      if (footer) footer.style.removeProperty('display');
      if (main) main.classList.add('pb-4', 'mb-4');
    };
  }, []);

  // Nodes for the rollup (ids + der content). Metric-independent, so fixed to
  // 'overall'; the chart computes its own colored nodes from the live metric.
  const chartNodes = useMemo(
    () => prepareChartData(movie, includeScenes, 'overall').nodes,
    [movie, includeScenes]
  );
  // Table always needs scenes regardless of the sunburst's Include-Scenes toggle.
  const tableNodes = useMemo(() => prepareChartData(movie, true, 'overall').nodes, [movie]);
  const available = useMemo(() => availableGrains(chartNodes), [chartNodes]);
  // The per-grain habit rollups come from the der_rollup pass (movie_details.der_result.rollups),
  // passed through by the controller. No client-side derivation.
  const rollups = movie.rollups || {};

  const resolveUnitNode = useCallback((unit, pool = tableNodes) => {
    const nodes = pool || tableNodes;
    const rawId = unit && typeof unit === 'object' ? (unit.id || unit.unit_id || unit.unitId) : null;
    if (rawId) {
      const byId = nodes.find(x => {
        const dc = x.detailedContent || {};
        return x.id === rawId || dc.id === rawId || dc.unit_id === rawId || dc.unitId === rawId;
      });
      if (byId) return byId;
    }

    const wanted = normalizeUnitText(unitChipLabel(unit));
    if (!wanted) return null;
    const exact = nodes.filter(x => unitTextCandidates(x).some(c => normalizeUnitText(c) === wanted));
    if (exact.length === 1) return exact[0];
    if (exact.length > 1) return exact.find(x => x.nodeType === (unit?.grain || unit?.type)) || null;

    const sceneMatch = wanted.match(/\bscene\s+(\d+)\b|\bsc\s*(\d+)\b/);
    if (sceneMatch) {
      const n = sceneMatch[1] || sceneMatch[2];
      return nodes.find(x => x.nodeType === 'scene' && String(x.detailedContent?.number) === String(n)) || null;
    }
    return null;
  }, [tableNodes]);

  // Sunburst wedge click → open that node's detail. Stable identity so the
  // chart effect doesn't re-plot on every render (it's an effect dependency).
  // The dim the lit synthesis pattern is about (its primary axis), so a click on a
  // footprinted node lands directly on that axis instead of the generic overview.
  const activeDim = footprintIds.length
    ? (synthesis?.writer_view?.tier_2_diagnosis?.controlling_pattern?.axis_chain?.[0] || null)
    : null;
  const handleSelect = useCallback((node, currentMetric, id) => {
    // Interaction tracking: wheel/wedge click → which unit (scene number etc.).
    try {
      window.srTrack?.('sos_wheel_click', 'pcr', {
        unit_type: node?.type ?? null,
        number: node?.number ?? null,
        metric: currentMetric || null,
        id: id || null,
      });
    } catch (e) {}
    setSelectedNode({
      ...node,
      selectedMetric: currentMetric && currentMetric !== 'overall' ? currentMetric : null,
      // when a synthesis footprint is lit, open the node ON the pattern's named dim
      ...(activeDim ? { focusAxis: activeDim, targetAxis: activeDim } : {}),
    });
    setSelectedId(id || null);
    setCameFromRollup(false); // direct wedge click — not from the rollup
    setView('node');
    openMobileDetail();
  }, [activeDim, openMobileDetail]);

  // Interaction tracking: count clicks in the right read-pane / detail modal, each
  // tagged with the unit currently loaded there — i.e. the previous (left/wheel)
  // selection. So a right-pane click tells us what they were reading about.
  const trackPaneClick = useCallback((zone) => {
    try {
      window.srTrack?.('sos_' + zone + '_click', 'pcr', {
        number: selectedNode?.number ?? null,
        unit_type: selectedNode?.type ?? null,
      });
    } catch (e) {}
  }, [selectedNode]);

  // Rollup unit click → select its wedge (focus ring) + open its detail.
  // `targetAxis` carries the axis the rollup named so the node panel can scroll
  // to it (lands precisely once the 12-axis node panel ships; the legacy panel
  // ignores it).
  const handleSelectUnit = useCallback((unit) => {
    const n = resolveUnitNode(unit, tableNodes);
    if (!n || !n.detailedContent) return;
    setSelectedNode({ ...n.detailedContent, targetAxis: (unit && typeof unit === 'object') ? (unit.axis || null) : null });
    setSelectedId(n.id);
    setCameFromRollup(true); // opened from a rollup unit — offer Back to rollup
    setView('node');
    openMobileDetail();
  }, [resolveUnitNode, tableNodes, openMobileDetail]);

  const backToRollup = useCallback(() => {
    setMobileDetailOpen(false);
    setView('rollup');
    setSelectedId(null);
    setSelectedNode(null);
  }, []);

  // Grain tab / overview-card click → that grain's rollup.
  const goToGrain = useCallback((g) => { setMobileDetailOpen(false); setRollupGrain(g); setView('rollup'); }, []);

  // Table cell click → open that axis in the SAME right-pane node panel (axis=null → unit overview).
  const handleSelectAxis = useCallback((n, axis) => {
    if (!n || !n.detailedContent) return;
    setSelectedNode({
      ...n.detailedContent,
      focusAxis: axis || null,
      targetAxis: axis || (n.detailedContent.repair || {}).axis || null,
    });
    setSelectedId(n.id);
    setCameFromRollup(false); // table cell opens the node directly — not from the rollup
    setView('node');
    setMobileDetailOpen(false);
    setTableDrawer(true);   // table mode: clicking a cell opens the detail drawer
  }, []);

  // Table mode: clicking a grain tab opens the drawer on that rollup.
  const openRollupDrawer = useCallback((g) => {
    setMobileDetailOpen(false); setRollupGrain(g); setView('rollup'); setSelectedNode(null); setSelectedId(null); setTableDrawer(true);
  }, []);
  const closeTableDrawer = useCallback(() => { setTableDrawer(false); setMobileDetailOpen(false); setSelectedNode(null); setSelectedId(null); }, []);

  // Escape closes the table detail drawer (only while it's open). A repair modal
  // rendered elsewhere manages its own Escape and stops propagation.
  useEffect(() => {
    if (!tableDrawer) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') closeTableDrawer(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [tableDrawer, closeTableDrawer]);

  // Switching the left perspective (sunburst↔table). Table starts with the drawer hidden
  // (full-width grid); the sunburst keeps its persistent right panel.
  const switchView = useCallback((v) => { setLeftView(v); setSelectedNode(null); setSelectedId(null); setView('rollup'); setTableDrawer(false); setMobileDetailOpen(false); }, []);

  // B3 — prev/next stepping through the units of the selected node's grain
  // (e.g. walk scenes 25→30 through a dead stretch).
  const nodeGrain = selectedNode?.type;
  const activeNodes = leftView === 'table' ? tableNodes : chartNodes;
  const grainUnits = useMemo(
    () => (nodeGrain && nodeGrain !== 'movie')
      ? activeNodes.filter(n => n.nodeType === nodeGrain && n.detailedContent?.number != null)
          .sort((a, b) => unitSortKey(a) - unitSortKey(b))
      : [],
    [activeNodes, nodeGrain]
  );
  // Same-grain peers' score maps — the within-script percentile baseline for the
  // node panel (corpus-wide percentiles slot in here later, once the vault's scored).
  const peers = useMemo(
    () => nodeGrain
      ? activeNodes.filter(n => n.nodeType === nodeGrain).map(n => n.detailedContent?.scores).filter(Boolean)
      : [],
    [activeNodes, nodeGrain]
  );
  const curIdx = useMemo(() => grainUnits.findIndex(n => n.id === selectedId), [grainUnits, selectedId]);

  // Level highlight: when a CONCRETE grain rollup is the home view (Scenes /
  // Sequences / Acts rollup, not the Overview) and no single node is selected,
  // soft-highlight every wedge of that grain on the wheel so the writer sees
  // which ring the rollup describes. Empty otherwise (single-node focus ring
  // takes over once a node is selected).
  const levelIds = useMemo(() => {
    if (view !== 'rollup') return [];
    if (!['scene', 'sequence', 'act'].includes(rollupGrain)) return [];
    return chartNodes.filter(n => n.nodeType === rollupGrain).map(n => n.id);
  }, [view, rollupGrain, chartNodes]);
  // Footprint mode: map a pattern's der refs (der:unit:<grain>:<from>_<to>) to wheel node ids by
  // scene number — every scene whose number falls in any ref's range lights up, so the pattern's
  // cross-level footprint is visible across the rings. (Act/sequence wedge styling is v1.1.)
  const highlightFootprint = useCallback((refs, cardIdx) => {
    const nums = new Set();
    (refs || []).forEach(r => {
      const m = typeof r === 'object' ? r.ref : r;
      const mm = m && m.match(/der:unit:(?:scene|sequence|act|script):(\d+)_(\d+)/);
      if (mm) { for (let n = +mm[1]; n <= +mm[2]; n++) nums.add(n); }
    });
    // Light the footprint scenes AND, by walking each scene's parent chain, the sequence and act
    // that contain them — so the cross-level footprint shows on every ring, not just scenes.
    const byId = new Map(chartNodes.map(n => [n.id, n]));
    const ids = new Set();
    chartNodes.filter(n => n.nodeType === 'scene' && nums.has(n.detailedContent?.number)).forEach(n => {
      ids.add(n.id);
      let p = n.parent, guard = 0;
      while (p && p !== 'Movie' && guard++ < 4) { ids.add(p); p = byId.get(p)?.parent; }
    });
    setFootprintIds([...ids]);
    setActiveCardIdx(cardIdx);
  }, [chartNodes]);
  const clearFootprint = useCallback(() => { setFootprintIds([]); setActiveCardIdx(null); }, []);

  // Push node ids to the sunburst's level-highlight channel. Footprint highlight (a clicked synthesis
  // pattern) takes precedence; otherwise the active grain rollup's wedges. Sunburst perspective only.
  useEffect(() => {
    if (leftView !== 'sunburst') { setLevelHighlight([]); return; }
    setLevelHighlight(footprintIds.length ? footprintIds : levelIds);
  }, [levelIds, leftView, footprintIds]);

  const stepTo = useCallback((i) => {
    const n = grainUnits[i];
    if (!n || !n.detailedContent) return;
    setSelectedNode({ ...n.detailedContent, targetAxis: (n.detailedContent.repair || {}).axis || null });
    setSelectedId(n.id);
    setCameFromRollup(true); // stepping walks the grain's units — a rollup-rooted flow
    openMobileDetail();
  }, [grainUnits, openMobileDetail]);

  // Breadcrumb — the navigation spine. Walk the parent chain from the selected node up to
  // the script root, so a writer can climb scene → sequence → act → script and back. This
  // is what replaces grain tabs as the way to move between levels (Phase 1).
  const crumbLabel = (n) => {
    if (n.nodeType === 'movie') return 'Script';
    return unitDisplayLabel(n, 'breadcrumb') || n.label || '';
  };
  const crumbs = useMemo(() => {
    if (!selectedId) return [];
    const byId = new Map(activeNodes.map(n => [n.id, n]));
    const chain = [];
    let cur = byId.get(selectedId);
    let guard = 0;
    while (cur && guard++ < 12) { chain.unshift(cur); cur = cur.parent ? byId.get(cur.parent) : null; }
    return chain;
  }, [activeNodes, selectedId]);
  const selectNodeById = useCallback((id) => {
    const n = activeNodes.find(x => x.id === id);
    if (!n || !n.detailedContent) return;
    setSelectedNode({ ...n.detailedContent });
    setSelectedId(id);
    setView('node');
    openMobileDetail();
  }, [activeNodes, openMobileDetail]);
  // ‹ Back — return from a node detail to whichever right-pane tab you drilled from (keeps the tab
  // and any lit footprint). ⌂ Main read — always jump home to the top synthesis and reset the wheel.
  const backToTab = useCallback(() => {
    setMobileDetailOpen(false);
    setView('rollup'); setSelectedNode(null); setSelectedId(null);
  }, []);
  const homeToMainRead = useCallback(() => {
    setMobileDetailOpen(false);
    setRightTab(synthesis ? 'synthesis' : 'patterns');
    setView('rollup'); setSelectedNode(null); setSelectedId(null);
    setFootprintIds([]); setActiveCardIdx(null); setRollupGrain('overview');
  }, [synthesis]);

  // The rollup/node body — shared by the sunburst's right column AND the table's drawer.
  // A rollup pattern names its units as labels (or {id,label}). Resolve to a node and open
  // it — clicking "Scene 9" in a pattern jumps you straight to that scene's read.
  const openUnitByLabel = useCallback((u) => {
    const n = resolveUnitNode(u, tableNodes);
    if (n && n.detailedContent) {
      setSelectedNode({ ...n.detailedContent });
      setSelectedId(n.id);
      setCameFromRollup(true);
      setView('node');

      if (isPhone || leftView === 'sunburst') {
        setTableDrawer(false);
        openMobileDetail();
      } else {
        setMobileDetailOpen(false);
        setTableDrawer(true);
      }
    }
  }, [isPhone, leftView, openMobileDetail, resolveUnitNode, tableNodes]);

  const panelBody = view === 'rollup' ? (
    <RollupPanel
      rollups={rollups} grain={rollupGrain} available={available}
      script={movie.movie} onGrainChange={goToGrain} onSelectUnit={openUnitByLabel}
    />
  ) : (
    <>
      {/* (Breadcrumb now lives in the sticky nav-shell header above — not duplicated here.) */}
      <div className="d-flex align-items-center gap-2 px-3 pt-2 flex-wrap">
        {cameFromRollup && (
          <button type="button" onClick={backToRollup}
                  className="btn btn-sm btn-link text-decoration-none px-0" style={{ color: NAVY, fontWeight: 600 }}>
            ‹ Back to {grainHeading(movie.movie, rollupGrain)} rollup
          </button>
        )}
        {grainUnits.length > 1 && curIdx >= 0 && (
          <div className="ms-auto d-flex align-items-center gap-2">
            <button type="button" disabled={curIdx <= 0} onClick={() => stepTo(curIdx - 1)}
                    className="btn btn-sm btn-outline-secondary py-0 px-2" aria-label="Previous unit">‹</button>
            <span className="small text-muted">{curIdx + 1} / {grainUnits.length}</span>
            <button type="button" disabled={curIdx >= grainUnits.length - 1} onClick={() => stepTo(curIdx + 1)}
                    className="btn btn-sm btn-outline-secondary py-0 px-2" aria-label="Next unit">›</button>
          </div>
        )}
      </div>
      <NodePanel node={selectedNode} peers={peers} movieHash={movie.hash} movieId={null} script={movie.movie}/>
    </>
  );

  const mobileNodeTitle = crumbs.length
    ? crumbLabel(crumbs[crumbs.length - 1])
    : (unitDisplayLabel(selectedNode, 'breadcrumb') || 'Detail');
  const mobileNodeModal = isPhone && mobileDetailOpen && view === 'node' ? (
    <div className="d-flex flex-column"
         role="dialog"
         aria-modal="true"
         aria-label={`${mobileNodeTitle} detail`}
         onClickCapture={() => trackPaneClick('modal')}
         style={{ position: 'fixed', inset: 0, width: '100%', maxWidth: '100%', background: '#fff', zIndex: 1001 }}>
      <div className="flex-shrink-0 d-flex align-items-center px-3 pt-2 pb-2"
           style={{ borderBottom: '1px solid #e2e8f0', minHeight: HEADER_H, gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: '#64748b', fontSize: '.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em' }}>Detail</div>
          <div style={{ color: NAVY, fontWeight: 800, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {mobileNodeTitle}
          </div>
        </div>
        <button type="button" onClick={closeMobileDetail}
                className="btn btn-sm btn-link text-decoration-none ms-auto px-0"
                style={{ color: NAVY, fontWeight: 700, flexShrink: 0 }}>✕ Close</button>
      </div>
      <div className="flex-grow-1 overflow-auto" style={{ minHeight: 0 }}>{panelBody}</div>
    </div>
  ) : null;

  // ── Guided tour (Shepherd) ─────────────────────────────────────────────────
  // Auto-runs once on first visit; always re-launchable from the "Take a tour" button.
  const tourRef = useRef(null);
  const startTour = useCallback(() => {
    setLeftView('sunburst');   // the tour targets the wheel view
    setMetric('overall');
    // Let the view switch + wheel render before Shepherd attaches to #sos-wheel etc.
    setTimeout(() => {
      try { if (tourRef.current && tourRef.current.isActive && tourRef.current.isActive()) tourRef.current.complete(); } catch (e) { /* ignore */ }
      const t = buildSosTour();
      tourRef.current = t;
      t.start();
    }, 400);
  }, [setLeftView, setMetric]);

  // First-visit auto-launch (desktop only). The SoS view is reached via a menu, so it may be
  // mounted-but-hidden on page load — don't fire on mount. Poll until the wheel is actually
  // VISIBLE (has a layout box and isn't display:none), i.e. the moment the menu reveals it,
  // then start once. Also covers the case where SoS is the landing view (visible immediately).
  useEffect(() => {
    if (phoneNow() || !shouldAutoStartSosTour()) return undefined;
    let done = false;
    const isVisible = (el) => !!el && el.offsetParent !== null && el.getClientRects().length > 0;
    const poll = setInterval(() => {
      if (done) return;
      if (isVisible(document.querySelector('#sos-wheel'))) {
        done = true;
        clearInterval(poll);
        startTour();
      }
    }, 500);
    const stop = setTimeout(() => clearInterval(poll), 120000);   // give up after 2 min
    return () => { clearInterval(poll); clearTimeout(stop); };
  }, [startTour]);

  // Site-wide "What's this?" orientation strip — one plain line, expandable to a
  // full how-to-read, shown above the page's own chrome so a cold arrival is
  // oriented before the colour and rings hit them.
  const pageIntro = (
    <>
      {/* Early-preview flag — the analysis is still being tuned; set expectations honestly. */}
      <div style={{ background: '#fff8e6', borderBottom: '1px solid #f0e2b6', padding: '.45rem 1rem',
                    display: 'flex', alignItems: 'center', gap: 10, fontSize: '.85rem', color: '#7a5b00' }}>
        <span style={{ background: '#b8860b', color: '#fff', fontWeight: 800, fontSize: '.66rem', letterSpacing: '.06em',
                       borderRadius: 4, padding: '2px 7px', flexShrink: 0 }}>BETA</span>
        <span>Layered Read is an early preview — the analysis is still being tuned, so expect the occasional rough edge or off note. Treat it as a guide, not gospel.</span>
      </div>
    <PageIntro
      title="Layered Read"
      heading="What you’re looking at"
      action={
        <button type="button" onClick={startTour}
          style={{ background: NAVY, color: '#fff', border: 'none', borderRadius: 999,
                   padding: '6px 16px', fontWeight: 700, fontSize: '.9rem', cursor: 'pointer',
                   boxShadow: '0 2px 8px rgba(20,83,110,.25)' }}>
          ▶ Take the Layered Read tour
        </button>
      }
      summary={
        <>
          Your whole script, read on three things — <strong>Design</strong> (is it built),{' '}
          <strong>Execution</strong> (does it play on the page), and <strong>Read</strong> (does it grip) —
          then mapped scene by scene. Colour shows where each part is strong or needs work.
        </>
      }>
      <p className="mb-2">
        The rings nest from the centre out: the middle is your <strong>whole script</strong>,
        then <strong>acts</strong>, then <strong>sequences</strong>, then the outer ring of{' '}
        <strong>scenes</strong> — reading clockwise from the top.
      </p>
      <p className="mb-2">
        <strong style={{ color: NAVY }}>Colour is a read, not a verdict.</strong>{' '}
        Warm reds mean a part is working harder than it’s landing; greens mean it’s landing well;
        amber is in between. Nothing here is pass/fail — it’s a map of where to look.
      </p>
      <p className="mb-0">
        <strong style={{ color: NAVY }}>To explore:</strong> click any wedge to open its detail read —
        what’s working, what’s dragging, and your options. <strong>Double-click an act
        or sequence to zoom into it</strong> (double-click the centre to zoom back out). Use{' '}
        <em>Colour by</em> (top) to recolour the whole wheel by a single craft signal, or switch to{' '}
        <em>Table</em> for the same scores as a sortable list.
      </p>
    </PageIntro>
    </>
  );

  // PATTERNS perspective — full-width. The cross-cutting read (the rollup) gets the whole
  // width; the grain selector (Script/Acts/Sequences/Scenes) lives here as the "which level"
  // control. (Cross-level axis patterns + opening units in a drawer are Phase 2/3.)
  if (leftView === 'patterns') {
    return (
      <div className="container-fluid px-3" ref={rootRef} style={{ minHeight: fitH }}>
        {pageIntro}
        <div className="px-3 pt-2 pb-2 d-flex align-items-center gap-3 flex-wrap" style={{ borderBottom: '1px solid #e2e8f0', minHeight: HEADER_H }}>
          <Controls includeScenes={includeScenes} onToggle={toggleScenes}
                    metric={metric} onMetricChange={setMetric}
                    view={leftView} onViewChange={switchView}
                    dimNotOwed={dimNotOwed} onToggleDim={() => setDimNotOwed(v => !v)}
                    onOpenWiki={setWikiArt} prominentScenes={isPhone}/>
          {!patternAxis && (
            <div className="flex-shrink-0">
              <GrainTabs grain={rollupGrain} available={available} onChange={goToGrain} script={movie.movie} />
            </div>
          )}
          {/* Look at: the habit rollup (default), or ONE axis across every level. */}
          <div className="flex-shrink-0 d-flex align-items-center" style={{ gap: 6 }}>
            <label htmlFor="pattern-axis" style={{ fontWeight: 600, color: NAVY, fontSize: 13, whiteSpace: 'nowrap' }}>Look at</label>
            <select id="pattern-axis" className="form-select form-select-sm w-auto" value={patternAxis || ''}
                    onChange={e => setPatternAxis(e.target.value || null)}
                    style={{ borderColor: NAVY, color: NAVY, fontWeight: 600, cursor: 'pointer' }}>
              <option value="">Habits (rollup)</option>
              <optgroup label="One axis, across levels">
                {['contract', 'pressure', 'development', 'movement', 'consequence', 'economy', 'assembly', 'clarity', 'freshness', 'unpredictability', 'charge', 'grip'].map(k => (
                  <option key={k} value={k}>{METRIC_LABELS[k]}</option>
                ))}
              </optgroup>
            </select>
          </div>
        </div>
        <div className="px-3 pb-4" style={{ maxWidth: 1000, margin: '0 auto' }}>
          {patternAxis ? (
            <AxisPatterns nodes={tableNodes} axis={patternAxis} script={movie.movie} onOpenUnit={(u) => {
              setSelectedNode({ ...u.detailedContent });
              setSelectedId(u.id);
              setCameFromRollup(false);
              setView('node');
              if (isPhone) {
                setTableDrawer(false);
                setMobileDetailOpen(true);
              } else {
                setMobileDetailOpen(false);
                setTableDrawer(true);
              }
            }} />
          ) : (
            <RollupPanel
              rollups={rollups} grain={rollupGrain} available={available}
              script={movie.movie} onGrainChange={goToGrain} onSelectUnit={openUnitByLabel}
            />
          )}
        </div>
        {/* Unit DRAWER over Patterns — clicking a scene/sequence opens its read here, so the
            patterns stay visible behind it; the modal-fix flow lives inside panelBody. */}
        {tableDrawer && (
          <>
            <div onClick={closeTableDrawer} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.18)', zIndex: 1000 }} />
            <div className="d-flex flex-column" data-sos-unit-drawer
                 style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 580, maxWidth: '94%',
                          background: '#fff', borderLeft: '1px solid #e2e8f0', boxShadow: '-8px 0 24px rgba(15,23,42,.14)', zIndex: 1001 }}>
              <div className="flex-shrink-0 d-flex align-items-center px-3 pt-2 pb-2" style={{ borderBottom: '1px solid #e2e8f0', minHeight: HEADER_H }}>
                <span style={{ fontWeight: 600, color: NAVY }}>Detail</span>
                <button type="button" onClick={closeTableDrawer}
                        className="btn btn-sm btn-link text-decoration-none ms-auto px-0" style={{ color: NAVY, fontWeight: 600 }}>✕ Close</button>
              </div>
              <div className="flex-grow-1 overflow-auto" style={{ minHeight: 0 }}>{panelBody}</div>
            </div>
          </>
        )}
        {mobileNodeModal}
      </div>
    );
  }

  // TABLE perspective — full-width grid; the detail drawer is hidden until the writer
  // clicks a grain tab (→ rollup) or a table cell (→ node). Closing returns to the grid.
  if (leftView === 'table') {
    return (
      <div className="container-fluid px-3" ref={rootRef} style={{ height: fitH }}>
        <div className="d-flex flex-column h-100">
          {pageIntro}
          <div className="flex-shrink-0 px-3 pt-2 pb-2 d-flex align-items-center gap-3" style={{ borderBottom: '1px solid #e2e8f0', minHeight: HEADER_H }}>
            <Controls includeScenes={includeScenes} onToggle={toggleScenes}
                      metric={metric} onMetricChange={setMetric}
                      view={leftView} onViewChange={switchView}
                      dimNotOwed={dimNotOwed} onToggleDim={() => setDimNotOwed(v => !v)}
                      onOpenWiki={setWikiArt} prominentScenes={isPhone}/>
            <div className="flex-shrink-0">
              <GrainTabs grain={tableDrawer && view === 'rollup' ? rollupGrain : null} available={available} onChange={openRollupDrawer} script={movie.movie} />
            </div>
          </div>
          <div className="flex-grow-1 position-relative" style={{ minHeight: 0 }}>
            <TableView nodes={tableNodes} onSelectAxis={handleSelectAxis} selectedId={selectedId} script={movie.movie}/>
            {tableDrawer && (
              <div className="d-flex flex-column"
                   style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 560, maxWidth: '94%',
                            background: '#fff', borderLeft: '1px solid #e2e8f0', boxShadow: '-8px 0 24px rgba(15,23,42,.10)', zIndex: 20 }}>
                <div className="flex-shrink-0 d-flex align-items-center px-3 pt-2 pb-2" style={{ borderBottom: '1px solid #e2e8f0', minHeight: HEADER_H }}>
                  <span style={{ fontWeight: 600, color: NAVY }}>{view === 'rollup' ? `${grainHeading(movie.movie, rollupGrain)} rollup` : 'Detail'}</span>
                  <button type="button" onClick={closeTableDrawer}
                          className="btn btn-sm btn-link text-decoration-none ms-auto px-0" style={{ color: NAVY, fontWeight: 600 }}>✕ Close</button>
                </div>
                <div className="flex-grow-1 overflow-auto" style={{ minHeight: 0 }}>{panelBody}</div>
              </div>
            )}
          </div>
        </div>
        {mobileNodeModal}
      </div>
    );
  }

  // SUNBURST perspective — the wheel is full-size and flows in normal document order, so
  // the PAGE grows and scrolls to reveal all of it (no cramped inner scroll box). The right
  // panel is STICKY so the diagnosis stays in view and scrolls within itself while you
  // scroll the tall wheel on the left.
  return (
    <div className="container-fluid px-3" ref={rootRef} style={{ minHeight: fitH }}>
      {/* The reusable PageIntro banner is the on-ramp ("what this is", short + expandable), then Zone A
          (the headline read + its ring) and Zone B (the transition into the tool). */}
      {pageIntro}
      {synthesis && (<div id="sos-read">
        <InsightList synthesis={synthesis} movie={movie} onExplore={exploreMetric} />
        <ReadTransition />
      </div>)}
      <div className="row g-0 align-items-start" id="sos-wheel-row">
        <div className="col-12 col-md-6 order-2 order-md-1">
          <div className="px-3 pt-2 pb-2 d-flex align-items-center" style={{ borderBottom: '1px solid #e2e8f0', minHeight: HEADER_H }}>
            <Controls includeScenes={includeScenes} onToggle={toggleScenes}
                      metric={metric} onMetricChange={setMetric}
                      view={leftView} onViewChange={switchView}
                      dimNotOwed={dimNotOwed} onToggleDim={() => setDimNotOwed(v => !v)}
                      onOpenWiki={setWikiArt} prominentScenes={isPhone}/>
          </div>
          {/* legend sits directly under the colour control that drives it */}
          <div style={{ borderBottom: '1px solid #eef2f4' }}>
            <SunburstLegend metric={metric} onSwitchToTable={() => switchView('table')} />
          </div>
          {!includeScenes && (
            <div style={{ padding: '6px 12px', fontSize: 12, color: '#5b7a8c', background: '#f6f9fb', borderBottom: '1px solid #eef2f4' }}>
              Scenes are hidden — tap an act or sequence to zoom in and see its scenes.
            </div>
          )}
          <div className="position-relative" id="sos-wheel">
            <SunburstChart movie={movie} includeScenes={includeScenes} metric={metric} dimNotOwed={dimNotOwed}
                           onSelect={handleSelect} selectedId={selectedId} autoSelectRoot={false}/>
          </div>
        </div>
        <div className="col-12 col-md-6 order-1 order-md-2" id="sos-read-pane" ref={readPaneRef}
             onClickCapture={() => trackPaneClick('rightpane')}>
          <div style={isPhone ? { display: 'flex', flexDirection: 'column' } : { position: 'sticky', top: 0, maxHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            {/* Persistent nav shell — never trapped: ⌂ Main read (home) · ‹ Back (to the tab you drilled
                from) · breadcrumb (climb units). In the list views: the two tabs. Reset wheel clears the
                footprint highlight. */}
            <div className="px-3 d-flex align-items-center flex-wrap" style={{ borderBottom: '1px solid #e2e8f0', minHeight: HEADER_H, background: '#fff', gap: 8 }}>
              {view === 'node' && !isPhone ? (
                <nav className="d-flex align-items-center flex-wrap" style={{ gap: 5, fontSize: '.85rem' }} aria-label="Breadcrumb">
                  <button type="button" className="sos-crumb" onClick={homeToMainRead} style={{ all: 'unset', cursor: 'pointer', color: NAVY, fontWeight: 700 }}>⌂ The Read</button>
                  <span style={{ color: '#cbd5e1' }}>·</span>
                  <button type="button" className="sos-crumb" onClick={backToTab} style={{ all: 'unset', cursor: 'pointer', color: NAVY, fontWeight: 600 }}>‹ Back</button>
                  <span style={{ color: '#dbe3e8', margin: '0 2px' }}>|</span>
                  {crumbs.map((n, i) => {
                    const last = i === crumbs.length - 1;
                    return (
                      <React.Fragment key={n.id}>
                        {i > 0 && <span style={{ color: '#cbd5e1' }}>›</span>}
                        {last
                          ? <span style={{ color: '#0f172a', fontWeight: 700 }}>{crumbLabel(n)}</span>
                          : <button type="button" className="sos-crumb" onClick={() => selectNodeById(n.id)} style={{ all: 'unset', cursor: 'pointer', color: NAVY, fontWeight: 600 }}>{crumbLabel(n)}</button>}
                      </React.Fragment>
                    );
                  })}
                </nav>
              ) : (
                <>
                  {synthesis && (
                    <button type="button" onClick={() => setRightTab('synthesis')}
                            style={{ all: 'unset', cursor: 'pointer', padding: '.5rem .25rem', fontWeight: 700, fontSize: '.95rem',
                              color: rightTab === 'synthesis' ? NAVY : '#94a3b8',
                              borderBottom: rightTab === 'synthesis' ? `2px solid ${NAVY}` : '2px solid transparent' }}>The Read</button>
                  )}
                  <button type="button" onClick={() => setRightTab('patterns')}
                          style={{ all: 'unset', cursor: 'pointer', padding: '.5rem .25rem', fontWeight: 700, fontSize: '.95rem',
                            color: rightTab === 'patterns' ? NAVY : '#94a3b8',
                            borderBottom: rightTab === 'patterns' ? `2px solid ${NAVY}` : '2px solid transparent' }}>Patterns</button>
                </>
              )}
              <div className="d-flex align-items-center ms-auto" style={{ gap: 14 }}>
                {footprintIds.length > 0 && (
                  <button type="button" onClick={clearFootprint}
                          className="btn btn-sm btn-link text-decoration-none px-0"
                          style={{ color: '#5b7a8c', fontWeight: 600, fontSize: '.84rem' }}>Reset wheel</button>
                )}
                {/* Always-available help — opens the framework overview in the wiki drawer. */}
                <button type="button" id="sos-help" onClick={() => setWikiArt({ ...OVERVIEW.framework, kicker: 'How to read' })}
                        className="btn btn-sm btn-link text-decoration-none px-0 sos-crumb"
                        {...tip('How the Layered Read reads your script')}
                        style={{ color: NAVY, fontWeight: 600, fontSize: '.84rem' }}>ⓘ How to read this</button>
              </div>
            </div>
            <div className="overflow-auto" style={{ minHeight: 0 }}>
              {view === 'node' && !isPhone
                ? panelBody
                : (rightTab === 'synthesis' && synthesis)
                  ? <SynthesisPanel synthesis={synthesis} onHighlight={highlightFootprint}
                                    onClear={clearFootprint} activeCardIdx={activeCardIdx} onOpenWiki={setWikiArt} compact />
                  : <RollupPanel rollups={rollups} grain={rollupGrain} available={available}
                                 script={movie.movie} onGrainChange={goToGrain} onSelectUnit={openUnitByLabel} />}
            </div>
          </div>
        </div>
      </div>
      {/* Lifted wiki drawer — opened by the colour-by (i), the "How to read this" help link,
          and any InfoDot. Portals to <body>, so it overlays everything. */}
      {mobileNodeModal}
      <WikiDrawer article={wikiArt} onClose={() => setWikiArt(null)} onNavigate={setWikiArt} />
    </div>
  );
}

ScriptSnout.propTypes = {
  movie: PropTypes.object.isRequired,
};
